import React from 'react';
import { useConfig } from './config';

const RefundPolicy = () => {
  const config = useConfig();
  const lastUpdated = "November 14, 2024";

  return (
    <main>
      <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6 lg:px-8 bg-white rounded-lg shadow-xl my-8 relative">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500 rounded-full filter blur-3xl opacity-5 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-red-500 rounded-full filter blur-3xl opacity-5 translate-y-1/2 -translate-x-1/2"></div>
        
        {/* Content */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4 relative inline-block">
          Refund Policy
          <span className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-blue-500 rounded-full"></span>
        </h1>
        
        <p className="text-sm text-gray-600 mb-6">
          <strong>Last updated:</strong> {lastUpdated}
        </p>

        <p className="mb-4 text-gray-700">
          At <strong>{config.siteName}</strong>, we strive to ensure customer satisfaction with all our services. This Refund Policy outlines the conditions under which we offer refunds for your purchases.
        </p>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          1. Eligibility for Refunds
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          We may issue refunds under the following circumstances:
        </p>
        <ul className="list-disc list-inside mb-4 text-gray-700">
          <li>Technical issues that significantly impact the functionality of our services and cannot be resolved within a reasonable time</li>
          <li>Incorrect charges or billing errors</li>
          <li>Services not delivered as described</li>
          <li>Other circumstances as required by applicable laws and regulations</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          2. Refund Process
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          To request a refund, please contact our customer support team at <a href={`mailto:${config.contactEmail}`} className="text-blue-600 hover:text-blue-800 relative group">
            {config.contactEmail}
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
          </a> with the following information:
        </p>
        <ul className="list-disc list-inside mb-4 text-gray-700">
          <li>Your account information (username or email)</li>
          <li>Date of purchase</li>
          <li>Amount paid</li>
          <li>Reason for the refund request</li>
          <li>Any relevant supporting documentation</li>
        </ul>
        <p className="mb-4 text-gray-700">
          We will review your request and respond within 5 business days. If approved, refunds will be processed within 14 business days from the approval date. The time it takes for the refund to appear in your account depends on your payment method and financial institution.
        </p>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          3. Refund Timeframe
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          Refund requests must be submitted within 30 days from the date of purchase. Requests received after this period may not be eligible for a refund.
        </p>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          4. Non-Refundable Items and Services
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          The following items and services are generally non-refundable:
        </p>
        <ul className="list-disc list-inside mb-4 text-gray-700">
          <li>Virtual goods that have been consumed or used</li>
          <li>Subscription services after the cooling-off period</li>
          <li>Purchases made using promotional credits or bonus points</li>
          <li>Services where terms and conditions specifically state non-refundability</li>
        </ul>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          5. Refund Method
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          Refunds will generally be issued using the same payment method used for the original purchase. In certain cases, we may offer alternative refund methods.
        </p>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          6. Changes to This Policy
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          We reserve the right to update this Refund Policy at any time. Any changes will be effective immediately upon posting on our website. It is your responsibility to review this policy periodically for changes.
        </p>

        <h2 className="text-2xl font-semibold text-gray-800 mt-8 mb-4 relative inline-block">
          7. Contact Us
          <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-red-400 to-blue-400 rounded-full"></span>
        </h2>
        <p className="mb-4 text-gray-700">
          If you have any questions about our Refund Policy, please contact us at:
        </p>
        <p className="mb-4 text-gray-700">
          Email: <a href={`mailto:${config.contactEmail}`} className="text-blue-600 hover:text-blue-800 relative group">
            {config.contactEmail}
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
          </a>
        </p>
        <p className="mb-4 text-gray-700">
          Phone: <a href={`tel:${config.tel}`} className="text-blue-600 hover:text-blue-800 relative group">
            {config.tel}
            <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
          </a>
        </p>
        <p className="mb-4 text-gray-700">
          Address: {config.contactAddress}
        </p>
      </div>
    </main>
  );
};

export default RefundPolicy;